/* ---------------------------------------------------------------------------
   From a form of strings to an input the engine will accept, or a list of
   reasons it will not.

   WHY THIS IS ITS OWN FILE. It is the only place in the client that decides a
   half-filled form is not yet a bridge, and the only place a dotted field path
   is interpreted. Both are worth being able to test without a DOM — see
   scripts/cbplr.ts, which drives it directly.

   REFUSING IS THE POINT, not a validation nicety. Excel reads a blank cell as
   zero: leave the asphalt thickness empty and the 2009 workbook rates a bridge
   with no wearing course, cheerfully and with no indication. Every field here is
   required and an empty one produces a named refusal, so the form can say which
   values are still missing rather than computing something plausible from
   nothing.

   THE CLIENT COMPUTES FOR THE SCREEN; THE SERVER COMPUTES FOR THE RECORD. The
   same engine runs in both places, which is what makes the form's live preview
   trustworthy — but the number that is stored is the one the deployment worked
   out, never this one. See the header of functions/lib/ratings.ts.
   --------------------------------------------------------------------------- */

import type { PcdgInput, RcdgInput, RcfsInput } from '../cbplr/index.ts';
import { fieldsFor, type FieldSpec, type RatingDraft } from './store.ts';

/** Every field a given draft must have before anything can be computed. */
export function fieldsForDraft(draft: RatingDraft): readonly FieldSpec[] {
  return fieldsFor(draft.family, draft.position);
}

export function specFor(draft: RatingDraft, key: string): FieldSpec | null {
  return fieldsForDraft(draft).find((f) => f.key === key) ?? null;
}

/**
 * Write a value at a dotted path into a nested object, creating as it goes.
 *
 * Only ever called with the paths declared in `ALL_FIELDS`, which are literals
 * in this source tree — so there is no prototype-pollution surface here. The
 * guard is present anyway, because "only ever called with" is a claim about
 * today's callers.
 */
function setPath(target: Record<string, unknown>, path: string, value: number): void {
  const parts = path.split('.');
  let cursor = target;
  for (const [i, part] of parts.entries()) {
    if (part === '' || part === '__proto__' || part === 'constructor' || part === 'prototype') {
      throw new Error(`refusing to write the path "${path}"`);
    }
    if (i === parts.length - 1) {
      cursor[part] = value;
      return;
    }
    const next = cursor[part];
    if (typeof next !== 'object' || next === null) cursor[part] = {};
    cursor = cursor[part] as Record<string, unknown>;
  }
}

export interface Blocker {
  /** The field path, or `''` for something about the form as a whole. */
  key: string;
  /** One sentence, in the words the form prints beside the field. */
  why: string;
}

export interface Assembled {
  input: RcfsInput | RcdgInput | PcdgInput | null;
  /** Empty where `input` is non-null. Never both. */
  blockers: readonly Blocker[];
}

/**
 * A number typed into a field, or null.
 *
 * `Number('')` is 0, which is the whole reason this is not `Number(raw)`: an
 * empty field would become a real value and the refusal below would never fire.
 * `Number(' ')` is 0 too, and so is `Number('\n')`.
 */
export function readNumber(raw: string | undefined): number | null {
  if (raw === undefined) return null;
  const text = raw.trim();
  if (text === '') return null;
  const value = Number(text);
  return Number.isFinite(value) ? value : null;
}

/**
 * Turn the draft into an input, or say what is stopping it.
 *
 * Structural refusals come before per-field ones: a form with no structure
 * selected does not need twelve complaints about empty fields, it needs one
 * about the structure.
 */
export function assemble(draft: RatingDraft): Assembled {
  if (draft.bridgeId === null) {
    return { input: null, blockers: [{ key: '', why: 'Choose a structure to rate.' }] };
  }

  if ((draft.family === 'RCDG' || draft.family === 'PCDG') && draft.position === null) {
    return {
      input: null,
      blockers: [{ key: 'position', why: 'Say whether this is an interior or an exterior girder.' }],
    };
  }

  const blockers: Blocker[] = [];
  const built: Record<string, unknown> = {};

  /* RCDG's engine reads `position` off the input, and the API validates the
     cantilever against it. Written before the loop so a field named
     `position` could never be typed over it. */
  if (draft.family === 'RCDG' || draft.family === 'PCDG') built['position'] = draft.position;

  for (const spec of fieldsForDraft(draft)) {
    const value = readNumber(draft.fields[spec.key]);
    if (value === null) {
      blockers.push({
        key: spec.key,
        why:
          draft.fields[spec.key]?.trim()
            ? `${spec.label} is not a number.`
            : `${spec.label} has not been given.`,
      });
      continue;
    }
    /* Negative is refused for every field on this form. There is no dimension,
       area, unit weight or allowable stress on a flat slab that can be less than
       zero, and a minus sign here is a typing slip rather than a claim. Section
       loss of 0 is legitimate and passes. */
    if (value < 0) {
      blockers.push({ key: spec.key, why: `${spec.label} cannot be negative.` });
      continue;
    }
    setPath(built, spec.key, value);
  }

  /* Relationships the engine cannot check for itself, because each field is
     individually reasonable and the pair is not. All of them are the same slip —
     a value read off the wrong dimension line of a drawing — and all would
     otherwise produce a rating that looked ordinary.

     THE OVERALL DEPTH DIFFERS BY FAMILY, which is the point of doing this here
     rather than once for everything: a flat slab's steel sits inside its
     thickness, and a T-beam's sits inside the web PLUS the deck slab above it. */
  const thickness = readNumber(draft.fields['slabThickness']);
  const centroid = readNumber(draft.fields['centroidFromBottom']);
  const web = readNumber(draft.fields['webHeight']);
  const overall =
    draft.family === 'RCDG'
      ? thickness !== null && web !== null
        ? thickness + web
        : null
      : draft.family === 'PCDG'
        ? /* PCDG asks for neither of those: it has no `centroidFromBottom` — the
             tendon centroid is under `prestress` — so this check does not apply
             and the block below is skipped by `centroid` being null. */
          null
        : thickness;
  if (overall !== null && centroid !== null && centroid >= overall * 1000) {
    blockers.push({
      key: 'centroidFromBottom',
      why: `The reinforcement centroid (${centroid} mm) is at or above the top of a ${overall} m section, which leaves no effective depth.`,
    });
  }

  const carriageway = readNumber(draft.fields['carriageway']);
  const slabWidth = readNumber(draft.fields['slabWidth']);
  if (carriageway !== null && slabWidth !== null && carriageway > slabWidth) {
    blockers.push({
      key: 'carriageway',
      why: `The carriageway (${carriageway} m) is wider than the slab (${slabWidth} m).`,
    });
  }

  /* RCDG's own pair. A web wider than the girders are spaced is not a deck, and
     it would make the exterior girder's half-bay slab weight negative — a
     dead load that lightens the bridge. Neither value is wrong on its own. */
  if (draft.family === 'RCDG') {
    const bw = readNumber(draft.fields['girderWidth']);
    const spacing = readNumber(draft.fields['girderSpacing']);
    if (bw !== null && spacing !== null && bw >= spacing) {
      blockers.push({
        key: 'girderWidth',
        why: `The web (${bw} m) is as wide as the girder spacing (${spacing} m), which leaves no deck between girders.`,
      });
    }
  }

  /* PCDG's own three, and each is a value read off the wrong dimension line.

     THE TENDON MUST BE INSIDE THE GIRDER. A centroid at or above the soffit's
     opposite face leaves no eccentricity and the prestress stops being a
     prestress; at exactly the girder depth it is a straight axial force.

     THE TOP FLANGE MUST FIT ITS OWN PARTS. `LAYOUT!E47` derives the taper width
     as (flange − web − 2 × fillet) / 2, and a flange narrower than the web and
     its two fillets makes that negative — which the section table would happily
     integrate into a smaller area and a plausible-looking Iz.

     THE DECK CARRIED MUST REACH THE NEXT GIRDER. Less than the spacing on an
     interior girder means part of the bay is carried by nobody. */
  if (draft.family === 'PCDG') {
    const height = readNumber(draft.fields['section.height']);
    const tendon = readNumber(draft.fields['prestress.centroidFromSoffit']);
    if (height !== null && tendon !== null && tendon >= height * 1000) {
      blockers.push({
        key: 'prestress.centroidFromSoffit',
        why: `The tendon centroid (${tendon} mm) is at or above the top of a ${height} m girder, which leaves no eccentricity.`,
      });
    }

    const flange = readNumber(draft.fields['section.upperFlangeWidth']);
    const bw = readNumber(draft.fields['section.webWidth']);
    const fillet = readNumber(draft.fields['section.fillet']);
    if (flange !== null && bw !== null && fillet !== null && flange <= bw + 2 * fillet) {
      blockers.push({
        key: 'section.upperFlangeWidth',
        why: `The top flange (${flange} m) is no wider than the web and its two fillets (${bw + 2 * fillet} m), so the taper has negative width.`,
      });
    }

    const carried = readNumber(draft.fields['slabWidth']);
    const spacing = readNumber(draft.fields['girderSpacing']);
    if (
      draft.position === 'interior' &&
      carried !== null &&
      spacing !== null &&
      carried < spacing
    ) {
      blockers.push({
        key: 'slabWidth',
        why: `An interior girder carries a full bay, so the deck width (${carried} m) cannot be less than the spacing (${spacing} m).`,
      });
    }
  }

  if (blockers.length > 0) return { input: null, blockers };
  /* The bag was built from this family's own field list and its own dotted
     paths, so it has exactly that family's shape. The cast says so; the API
     re-reads every field server-side regardless, which is what actually
     guarantees it. */
  return { input: built as unknown as RcfsInput | RcdgInput | PcdgInput, blockers: [] };
}

/**
 * What the register already knows about this structure, as form values.
 *
 * DELIBERATELY THIN, and it is worth saying why rather than leaving it looking
 * unfinished. Three of the twelve dimensions come back and none of the six
 * material values or the reinforcement, because the cloud model and the register
 * genuinely do not carry them: the model store holds a glTF mesh tree and
 * component names, with no rebar schedule, no prestress and no material
 * strengths anywhere in it. Filling the rest would mean inventing it.
 *
 * That is the exporter's gap to close — the first of the handover's open
 * questions — and until it does, the form fills what exists, marks it `drawing`
 * because the register's inventory sheet is transcribed from the as-built
 * drawings, and leaves the rest empty and honest.
 */
export interface Prefill {
  fields: Record<string, string>;
  sources: Record<string, 'drawing' | 'era'>;
}

export function prefillFrom(bridge: {
  spanLength: number | null;
  length: number | null;
  spans: number | null;
  deckWidth: number | null;
  carriageway: number | null;
}): Prefill {
  const fields: Record<string, string> = {};
  const sources: Record<string, 'drawing' | 'era'> = {};

  const put = (key: string, value: number | null) => {
    if (value === null || !Number.isFinite(value) || value <= 0) return;
    fields[key] = String(value);
    sources[key] = 'drawing';
  };

  /* The single span, where the register records one. Falling back to
     length/spans is deliberately NOT done: an average span is not a span, and a
     rating computed on one would be wrong in a way nothing on the report would
     show. A structure with no span length recorded gets an empty field. */
  put('span', bridge.spanLength);
  put('slabWidth', bridge.deckWidth);
  put('carriageway', bridge.carriageway);

  return { fields, sources };
}

/**
 * The material values the era table gives for a year of construction.
 *
 * Offered as a fill and marked `era`, never applied silently — which is the
 * shape correction C8 is about. The workbook looks the inventory stresses up and
 * types the operating ones as literals; here both come from the same row or
 * neither does.
 */
export function eraFill(
  year: number | null,
  concrete: { strength: number; allowable: { inventory: number; operating: number } } | null,
  rebar: { strength: number; allowable: { inventory: number; operating: number } } | null,
): Prefill {
  const fields: Record<string, string> = {};
  const sources: Record<string, 'drawing' | 'era'> = {};
  if (year === null) return { fields, sources };

  const put = (key: string, value: number) => {
    fields[key] = String(value);
    sources[key] = 'era';
  };

  if (concrete) {
    put('concrete.strength', concrete.strength);
    put('concrete.allowable.inventory', concrete.allowable.inventory);
    put('concrete.allowable.operating', concrete.allowable.operating);
  }
  if (rebar) {
    put('rebar.strength', rebar.strength);
    put('rebar.allowable.inventory', rebar.allowable.inventory);
    put('rebar.allowable.operating', rebar.allowable.operating);
  }
  return { fields, sources };
}
